import { emitDebugEvent } from '@/features/copilot-debug';

import type {
  AddToolOutputFn,
  CopilotToolCall,
  CopilotToolDefinition,
  CopilotToolSpec,
} from './types';

export class CopilotToolRegistry {
  private readonly specs: CopilotToolSpec[];
  private readonly specByName: Map<string, CopilotToolSpec>;

  constructor(specs: CopilotToolSpec[]) {
    this.specs = specs;
    this.specByName = new Map(specs.map((s) => [s.name, s]));
  }

  hasSpec(toolName: string): boolean {
    return this.specByName.has(toolName);
  }

  getToolDefinitions(): CopilotToolDefinition[] {
    const defs: CopilotToolDefinition[] = [];
    for (const spec of this.specs) {
      const def = spec.getDefinition();
      if (def) {
        defs.push(def);
      }
    }
    return defs;
  }

  async handleToolCall(call: CopilotToolCall, addToolOutput: AddToolOutputFn): Promise<void> {
    const spec = this.specByName.get(call.toolName);
    if (!spec) {
      addToolOutput({
        state: 'output-error',
        toolCallId: call.toolCallId,
        tool: call.toolName,
        errorText: `No handler registered for tool: ${call.toolName}`,
      });
      return;
    }

    const parsedInput = spec.inputSchema.safeParse(call.input);
    if (!parsedInput.success) {
      const issues = parsedInput.error.issues;
      const issueLines = issues.map((issue) => {
        const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
        return `  - ${path}: ${issue.message}`;
      });
      const errorOutput = [
        `VALIDATION_ERROR: ${call.toolName} received invalid input (${issues.length} error${issues.length > 1 ? 's' : ''}):`,
        ...issueLines,
        '',
        'Re-read the tool definition and retry with correctly typed fields.',
      ].join('\n');
      emitDebugEvent('ai.toolCall.validated', `Validation failed: ${call.toolName}`, {
        toolCallId: call.toolCallId,
        toolName: call.toolName,
        success: false,
        issues: parsedInput.error.issues,
        rawInput: call.input,
      });
      addToolOutput({
        state: 'output-error',
        toolCallId: call.toolCallId,
        tool: call.toolName,
        errorText: errorOutput,
      });
      return;
    }

    emitDebugEvent('ai.toolCall.validated', `Validation passed: ${call.toolName}`, {
      toolCallId: call.toolCallId,
      toolName: call.toolName,
      success: true,
      parsedInput: parsedInput.data,
    });

    let result;
    try {
      result = await spec.handle(parsedInput.data, call.toolCallId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      emitDebugEvent('ai.toolCall.error', `Handler threw: ${call.toolName}`, {
        toolCallId: call.toolCallId,
        toolName: call.toolName,
        errorText: message,
      });
      addToolOutput({
        state: 'output-error',
        toolCallId: call.toolCallId,
        tool: call.toolName,
        errorText: `Tool handler error: ${message}`,
      });
      return;
    }

    if ('deferred' in result && result.deferred) {
      emitDebugEvent('ai.toolCall.deferred', `Deferred: ${call.toolName}`, {
        toolCallId: call.toolCallId,
        toolName: call.toolName,
      });
      return;
    }

    if ('errorText' in result) {
      emitDebugEvent('ai.toolCall.error', `Error: ${call.toolName}`, {
        toolCallId: call.toolCallId,
        toolName: call.toolName,
        errorText: result.errorText,
      });
      addToolOutput({
        state: 'output-error',
        toolCallId: call.toolCallId,
        tool: call.toolName,
        errorText: result.errorText,
      });
      return;
    }

    if ('output' in result) {
      emitDebugEvent('ai.toolCall.output', `Output: ${call.toolName}`, {
        toolCallId: call.toolCallId,
        toolName: call.toolName,
        output: result.output,
      });
      addToolOutput({
        toolCallId: call.toolCallId,
        tool: call.toolName,
        output: result.output,
      });
      return;
    }

    addToolOutput({
      toolCallId: call.toolCallId,
      tool: call.toolName,
      output: 'Tool completed with no output.',
    });
  }
}
